/**
 * Core Task Grouping Function
 *
 * Feature: 650-task-grouping
 *
 * Groups tasks using a provided strategy, returning groups and ungrouped tasks.
 */

import type { Task } from '@/hooks/graphql/useTaskMutationsGraphQL';
import type { TaskGroup, GroupingStrategy, GroupingResult } from '@/types/task-groups';

/**
 * Groups tasks using the provided strategy.
 *
 * @param tasks - Array of tasks to group
 * @param strategy - Grouping strategy to apply
 * @param minGroupSize - Minimum tasks required to form a group (default: 2)
 * @returns GroupingResult with groups, ungrouped tasks, and stats
 */
export function groupTasks(
  tasks: Task[],
  strategy: GroupingStrategy,
  minGroupSize = 2
): GroupingResult {
  // Group tasks by key
  const keyToTasks = new Map<string, Task[]>();
  const ungroupable: Task[] = [];

  for (const task of tasks) {
    const key = strategy.getGroupKey(task);
    if (key === null) {
      ungroupable.push(task);
      continue;
    }
    const existing = keyToTasks.get(key) || [];
    existing.push(task);
    keyToTasks.set(key, existing);
  }

  // Build groups (only for keys with enough tasks)
  const groups: TaskGroup[] = [];
  const ungrouped: Task[] = [...ungroupable];

  for (const [key, keyTasks] of keyToTasks) {
    if (keyTasks.length < minGroupSize) {
      ungrouped.push(...keyTasks);
      continue;
    }

    const pendingCount = keyTasks.filter(t => t.status === 'pending').length;
    const completedCount = keyTasks.filter(t => t.status === 'completed').length;
    const dismissedCount = keyTasks.filter(t => t.status === 'dismissed').length;
    const totalCount = keyTasks.length;

    groups.push({
      key,
      strategyId: strategy.id,
      label: strategy.getGroupLabel(key, keyTasks),
      url: strategy.getGroupUrl(key, keyTasks),
      actionType: keyTasks[0].actionType,
      sourceId: keyTasks[0].sourceId,
      tasks: keyTasks,
      pendingCount,
      completedCount,
      dismissedCount,
      totalCount,
      progressPercent: totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0,
      isComplete: pendingCount === 0,
      latestCreatedAt: keyTasks.reduce(
        (latest, t) => (t.createdAt > latest ? t.createdAt : latest),
        keyTasks[0].createdAt
      ),
    });
  }

  return {
    groups,
    ungrouped,
    strategyId: strategy.id,
    stats: {
      totalTasks: tasks.length,
      groupedTasks: tasks.length - ungrouped.length,
      ungroupedTasks: ungrouped.length,
      groupCount: groups.length,
    },
  };
}
